import React from "react";
import { Box, Typography } from "@mui/material";
import { THEME_PRIMARY, THEME_PRIMARY_TINT } from "../bookingTheme";
import { DATE_FORMATS } from "../../../constants";
import DefaultImage from "../../../assets/images/DefaultImage.png";
import {
  formatCurrentVehicleDetailValue,
  type CurrentBookingVehicleSummary,
} from "./bookingVehicleSummaryUtils";

function formatBookingAmountDisplay(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(Number(amount))) return "";
  return `₹${Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const BASE_IMAGE_URL = `${(process.env.REACT_APP_BASE_IMAGE_URL || "").replace(/\/?$/, "/")}`;

function resolvePreviewSrcForCard(value: string): string {
  const v = String(value || "").trim();
  if (!v) return DefaultImage;
  if (v.startsWith("http") || v.startsWith("data:")) return v;
  const cleaned = v.replace(/^\/+/, "");
  return cleaned ? `${BASE_IMAGE_URL}${cleaned}` : DefaultImage;
}

const summaryCardSx = {
  border: "1px solid rgba(22, 67, 135, 0.2)",
  borderRadius: 2,
  bgcolor: THEME_PRIMARY_TINT,
  px: { xs: 1.5, sm: 1.75 },
  py: { xs: 1.25, sm: 1.125 },
  boxShadow: "0 1px 2px rgba(22, 67, 135, 0.06)",
  minWidth: 0,
  width: "100%",
} as const;

const summaryCardTitleSx = {
  display: "block",
  fontWeight: 700,
  color: THEME_PRIMARY,
  fontSize: "0.7rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  mb: 0.75,
  pb: 0.5,
  borderBottom: `2px solid ${THEME_PRIMARY}`,
} as const;

function currentVehicleDetailsRow(label: string, rawValue: string) {
  const displayValue = formatCurrentVehicleDetailValue(rawValue);
  return (
    <Box
      key={label}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "minmax(100px, 36%) minmax(0, 1fr)", sm: "128px minmax(0, 1fr)" },
        columnGap: { xs: 1, sm: 1.25 },
        alignItems: "center",
        py: { xs: 0.65, sm: 0.55 },
        borderBottom: "1px solid rgba(22, 67, 135, 0.1)",
        "&:last-of-type": { borderBottom: "none", pb: 0 },
        "&:first-of-type": { pt: 0 },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          fontSize: { xs: "0.8125rem", sm: "0.875rem" },
          lineHeight: 1.35,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          fontSize: { xs: "0.875rem", sm: "0.9375rem" },
          lineHeight: 1.35,
          wordBreak: "break-word",
          minWidth: 0,
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
}

export interface CurrentVehicleSummaryCardProps {
  vehicle: CurrentBookingVehicleSummary;
}

const CurrentVehicleSummaryCard: React.FC<CurrentVehicleSummaryCardProps> = ({ vehicle }) => {
  const bookingAmountStr = formatBookingAmountDisplay(vehicle.bookingAmount);
  const planStartStr = DATE_FORMATS.formatDate(vehicle.planStartDate ?? "");
  const planEndStr = DATE_FORMATS.formatDate(vehicle.planEndDate ?? "");

  return (
  <Box sx={summaryCardSx}>
    <Typography variant="caption" sx={summaryCardTitleSx}>
      Current vehicle
    </Typography>
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 1.25, sm: 1.5 },
        alignItems: { xs: "stretch", sm: "flex-start" },
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", sm: 108 },
          height: { xs: 120, sm: 84 },
          flexShrink: 0,
          borderRadius: 1,
          bgcolor: "#fff",
          border: "1px solid rgba(22, 67, 135, 0.12)",
          alignSelf: { xs: "center", sm: "flex-start" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={resolvePreviewSrcForCard(vehicle.imageUrl ?? "")}
          alt={vehicle.vehicleName || "Current vehicle"}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            display: "block",
          }}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0 }}>
        {currentVehicleDetailsRow("Hub", vehicle.hubName ?? "")}
        {currentVehicleDetailsRow("Model", vehicle.modelName ?? "")}
        {currentVehicleDetailsRow("Vehicle name", vehicle.vehicleName)}
        {currentVehicleDetailsRow("Vehicle No.", vehicle.vehicleNum)}
        {currentVehicleDetailsRow("Registration No.", vehicle.registrationNumber)}
        {currentVehicleDetailsRow("Speed", vehicle.speed ?? "")}
        {currentVehicleDetailsRow("Range", vehicle.range ?? "")}
        {currentVehicleDetailsRow("Current plan", vehicle.currentPlanName ?? "")}
        {currentVehicleDetailsRow(
          "Booking amount",
          bookingAmountStr || ""
        )}
        {currentVehicleDetailsRow(
          "Plan start",
          planStartStr || ""
        )}
        {currentVehicleDetailsRow(
          "Plan end",
          planEndStr || ""
        )}
      </Box>
    </Box>
  </Box>
  );
};

export default CurrentVehicleSummaryCard;
